/**
 * reviewSearch.ts — text search + highlight over rendered document content.
 *
 * Extracted from HtmlWithAnchors so the classic and static renderers share one
 * implementation. It is inherently post-render (walks live DOM text nodes and
 * uses the CSS Custom Highlight API via app/unsafe), but it is NOT layout
 * measurement — it reads text, not geometry, so it's renderer-agnostic.
 *
 * The caller supplies the container element and the selector for the blocks to
 * search within. The classic view passes `[data-block-key]`; the static view
 * passes `[data-doc-content]` so only document text is searched and the comment
 * gutter (a grid sibling, not a descendant of content) is excluded.
 */

// @ts-ignore — unsafe.js is a plain JS module without types
import { registerHighlight, clearHighlights } from "../../../../app/unsafe";
import { HastNode, nodeText } from "./hast";
import { filterTableRows } from "./tableRowFilter";

export { clearHighlights };

// The pattern is whatever the user typed into a search box, not a regular
// expression. Without escaping, "(" threw SyntaxError from inside the caller's
// debounced handler, and "." or "*" quietly matched characters nobody typed —
// which also made the highlight disagree with the URS filter, whose matching is
// a plain substring test.
function escapeRegExp(literal: string): string {
    return literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Case-insensitive literal substring test — the AST-side twin of the highlight. */
export function matchesSearch(text: string, query: string): boolean {
    return new RegExp(escapeRegExp(query), "i").test(text);
}

/**
 * Drop table rows whose text doesn't contain `query` (header rows and rowspan
 * groups kept; a table whose only hit is its header stays whole). Empty query →
 * tree returned unchanged.
 */
export function filterTableRowsBySearch(nodes: HastNode[], query: string): HastNode[] {
    if (!query) return nodes;
    const regex = new RegExp(escapeRegExp(query), "i");
    return filterTableRows(nodes, (tr) => regex.test(nodeText(tr)), {
        keepTableIfHeaderMatches: true,
    });
}

export function findPatternInHtmlSubtree(root: Node, pattern: string): Range[] {
    const ranges: Range[] = [];
    const regex = new RegExp(escapeRegExp(pattern), "gi"); // case-insensitive

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let currentNode: Node | null;

    while ((currentNode = walker.nextNode())) {
        const textContent = currentNode.textContent || "";
        let match: RegExpExecArray | null;
        while ((match = regex.exec(textContent)) !== null) {
            const startOffset = match.index;
            const endOffset = startOffset + match[0].length;
            const range = document.createRange();
            range.setStart(currentNode, startOffset);
            range.setEnd(currentNode, endOffset);
            ranges.push(range);
        }
    }

    return ranges;
}

/**
 * Highlights every occurrence of `pattern` within the blocks matched by
 * `selector` inside `root`, toggling a `search-result` class on each matching
 * block. Returns the found ranges, or `undefined` when the pattern is empty
 * (highlights cleared). Mirrors the classic searchFn behaviour exactly.
 */
export function runHighlightSearch(
    root: HTMLElement,
    pattern: string,
    selector: string,
): Range[] | undefined {
    clearHighlights();
    const blocks = Array.from(root.querySelectorAll<HTMLElement>(selector));

    if (!pattern) {
        blocks.forEach((el) => el.classList.remove("search-result"));
        return undefined;
    }

    const findings: Range[] = [];
    for (const el of blocks) {
        const fs = findPatternInHtmlSubtree(el, pattern);
        el.classList.toggle("search-result", fs.length > 0);
        findings.push(...fs);
    }

    if (findings.length > 0) {
        registerHighlight("search-result", findings);
    }
    return findings;
}
